import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

type EquipeOpt = { id: number; nom: string; zoneId?: number | null; zone?: { id?: number; nom?: string | null } | null };

type RessourceRow = {
  id: number;
  nom: string;
  type: string;
  equipeId?: number | null;
  zoneId?: number | null;
  categorie?: string | null;
};

export type AffecterRessourcesEquipeDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  equipes: EquipeOpt[];
  initialEquipeId?: number | null;
};

function labelType(t: string): string {
  const map: Record<string, string> = {
    MATERIEL_ROULANT: "Matériel roulant",
    BAC_POUBELLE: "Bacs / poubelles",
    OUTIL: "Outils",
    EPI: "EPI",
  };
  return map[t] ?? t;
}

export default function AffecterRessourcesEquipeDrawer({
  open,
  onClose,
  onSaved,
  equipes,
  initialEquipeId = null,
}: AffecterRessourcesEquipeDrawerProps): React.JSX.Element | null {
  const [equipeId, setEquipeId] = useState<number | "">("");
  const [assigned, setAssigned] = useState<RessourceRow[]>([]);
  const [pool, setPool] = useState<RessourceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const selectedEquipe = useMemo(
    () => equipes.find((e) => e.id === equipeId),
    [equipes, equipeId]
  );

  const zoneIdForFilter = selectedEquipe?.zoneId ?? selectedEquipe?.zone?.id ?? undefined;

  const loadData = useCallback(async () => {
    if (equipeId === "") return;
    const eid = Number(equipeId);
    setLoading(true);
    try {
      const [rEq, rZone] = await Promise.all([
        api.get("/ressources-operationnelles", { params: { equipeId: eid } }),
        zoneIdForFilter != null
          ? api.get("/ressources-operationnelles", {
              params: { zoneId: zoneIdForFilter },
            })
          : Promise.resolve({ data: { data: [] as RessourceRow[] } }),
      ]);
      const onTeam = (rEq.data as { data?: RessourceRow[] })?.data ?? [];
      const inZone = (rZone.data as { data?: RessourceRow[] })?.data ?? [];
      setAssigned(onTeam);
      const poolRows = inZone.filter((r: RessourceRow) => !r.equipeId || r.equipeId === eid);
      const unassigned = poolRows.filter(
        (r: RessourceRow) => r.equipeId == null || r.equipeId === eid
      );
      setPool(unassigned.filter((r: RessourceRow) => r.equipeId == null));
    } catch {
      toast.error("Impossible de charger les ressources.", { toastId: "org-terrain-res-load" });
      setAssigned([]);
      setPool([]);
    } finally {
      setLoading(false);
    }
  }, [equipeId, zoneIdForFilter]);

  useEffect(() => {
    if (!open) {
      setEquipeId("");
      setAssigned([]);
      setPool([]);
      return;
    }
    if (initialEquipeId != null) {
      setEquipeId(initialEquipeId);
    }
  }, [open, initialEquipeId]);

  useEffect(() => {
    if (!open || equipeId === "") return;
    void loadData();
  }, [open, equipeId, loadData]);

  const assignToTeam = async (r: RessourceRow) => {
    if (equipeId === "") return;
    setBusyId(r.id);
    try {
      await api.put(`/ressources-operationnelles/${r.id}`, {
        equipeId: Number(equipeId),
      });
      toast.success("Ressource affectée à l’équipe.", { toastId: "org-res-assign-ok" });
      void loadData();
      onSaved();
    } catch {
      toast.error("Impossible de mettre à jour la ressource.", { toastId: "org-terrain-res-put" });
    } finally {
      setBusyId(null);
    }
  };

  const unassign = async (r: RessourceRow) => {
    setBusyId(r.id);
    try {
      await api.put(`/ressources-operationnelles/${r.id}`, {
        equipeId: null,
      });
      toast.success("Ressource retirée de l’équipe.", { toastId: "org-res-unassign-ok" });
      void loadData();
      onSaved();
    } catch {
      toast.error("Impossible de retirer la ressource.", { toastId: "org-terrain-res-un" });
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/60 backdrop-blur-sm">
      <div className="flex h-full w-full max-w-lg flex-col border-l border-slate-800 bg-slate-950 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Affecter des ressources</h2>
            <p className="mt-1 text-xs text-slate-500">
              Matériel roulant, bacs, outils, EPI — rattachement équipe via l’API ressources (PUT).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <label className="block text-sm font-medium text-slate-300">
            Équipe
            <select
              value={equipeId === "" ? "" : String(equipeId)}
              onChange={(e) => setEquipeId(e.target.value === "" ? "" : Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white"
            >
              <option value="">— Choisir —</option>
              {equipes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                  {e.zone?.nom ? ` · ${e.zone.nom}` : ""}
                </option>
              ))}
            </select>
          </label>

          {equipeId !== "" && zoneIdForFilter == null ? (
            <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
              Cette équipe n’a pas de zone — affectez d’abord une zone pour filtrer les ressources
              géographiques, ou utilisez la page Ressources opérationnelles.
            </p>
          ) : null}

          {equipeId !== "" && loading ? (
            <p className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </p>
          ) : equipeId !== "" ? (
            <>
              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Ressources de l’équipe
                </h3>
                <ul className="mt-2 space-y-2">
                  {assigned.length === 0 ? (
                    <li className="text-sm text-slate-500">Aucune ressource affectée.</li>
                  ) : (
                    assigned.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/80 px-3 py-2 text-sm text-slate-200"
                      >
                        <span>
                          {r.nom}
                          <span className="ml-2 text-xs text-slate-500">
                            {labelType(r.type)}
                            {r.categorie ? ` · ${r.categorie}` : ""}
                          </span>
                        </span>
                        <button
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => void unassign(r)}
                          className="text-xs font-medium text-rose-400 hover:text-rose-300 disabled:opacity-50"
                        >
                          Retirer
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              {zoneIdForFilter != null ? (
                <div className="mt-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Ressources disponibles (zone de l’équipe, non affectées)
                  </h3>
                  <ul className="mt-2 space-y-2">
                    {pool.length === 0 ? (
                      <li className="text-sm text-slate-500">
                        Aucune ressource libre dans cette zone. Créez-en depuis Ressources opérationnelles.
                      </li>
                    ) : (
                      pool.map((r) => (
                        <li
                          key={r.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-slate-800/80 px-3 py-2 text-sm"
                        >
                          <span>
                            {r.nom}
                            <span className="ml-2 text-xs text-slate-500">{labelType(r.type)}</span>
                          </span>
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            onClick={() => void assignToTeam(r)}
                            className="rounded-lg bg-emerald-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                          >
                            Affecter
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="border-t border-slate-800 p-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-slate-600 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
